import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { OwnerEventOverview, clubsBookingApi } from '@/api/clubs-booking';
import AdminShell from '@/components/admin/AdminShell';

const emptyForm = {
  childFirstName: '',
  childLastName: '',
  childDateOfBirth: '',
  parentName: '',
  amount: '',
};

const parseAmountToCents = (value: string) => {
  const normalized = value.replace(/[^0-9.]/g, '').trim();
  if (!normalized) return null;
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return Math.round(parsed * 100);
};

const ActivityWalkInPage = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [overview, setOverview] = useState<OwnerEventOverview | null>(null);
  const [selectedSessionId, setSelectedSessionId] = useState(searchParams.get('sessionId') || '');
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!eventId) return;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await clubsBookingApi.getMyEventOverview(eventId);
        setOverview(data);
      } catch (err: any) {
        const messageText = err?.response?.data?.message;
        setError(Array.isArray(messageText) ? messageText.join(', ') : messageText || 'Failed to load activity');
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [eventId]);

  useEffect(() => {
    if (!overview?.sessions.length) {
      setSelectedSessionId('');
      return;
    }

    const requestedSessionId = searchParams.get('sessionId');
    const nextSessionId =
      requestedSessionId && overview.sessions.some((session) => session.id === requestedSessionId)
        ? requestedSessionId
        : overview.sessions[0].id;

    setSelectedSessionId((current) => (current === nextSessionId ? current : nextSessionId));

    if (requestedSessionId !== nextSessionId) {
      setSearchParams({ sessionId: nextSessionId }, { replace: true });
    }
  }, [overview, searchParams, setSearchParams]);

  const selectedSession = useMemo(
    () => overview?.sessions.find((session) => session.id === selectedSessionId) || null,
    [overview, selectedSessionId],
  );

  const backHref = eventId
    ? `/activities/${eventId}${selectedSessionId ? `?sessionId=${encodeURIComponent(selectedSessionId)}` : ''}`
    : '/activities';

  const onSessionChange = (nextSessionId: string) => {
    setSelectedSessionId(nextSessionId);
    setSearchParams({ sessionId: nextSessionId }, { replace: true });
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!overview || !selectedSessionId) return;

    if (
      !form.childFirstName.trim() ||
      !form.childLastName.trim() ||
      !form.amount.trim()
    ) {
      setError('Enter the attendee name and amount paid');
      return;
    }

    const amountCents = parseAmountToCents(form.amount);
    if (amountCents === null) {
      setError('Enter a valid amount');
      return;
    }

    setSaving(true);
    setError('');

    try {
      await clubsBookingApi.createManualBooking({
        eventId: overview.event.id,
        sessionId: selectedSessionId,
        childFirstName: form.childFirstName,
        childLastName: form.childLastName,
        childDateOfBirth: form.childDateOfBirth || undefined,
        parentName: form.parentName || undefined,
        amountCents,
      });

      navigate(backHref, { replace: true });
    } catch (err: any) {
      const messageText = err?.response?.data?.message;
      setError(Array.isArray(messageText) ? messageText.join(', ') : messageText || 'Could not add walk-in attendee');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminShell
      title="Add walk-in attendee"
      subtitle={overview?.event.title || 'Create a manual booking for this session'}
      mobileTopRight={(
        <Link to={backHref} className="text-sm font-medium text-[#1F8F5F] hover:underline">
          Back
        </Link>
      )}
    >
      <div className="mb-2 hidden justify-end md:flex">
        <Link to={backHref} className="text-sm text-[#1F8F5F] hover:underline">
          Back
        </Link>
      </div>

      {loading && <p className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-500">Loading activity...</p>}
      {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      {!loading && overview && (
        <section className="rounded-xl border border-slate-200 bg-white p-3 sm:p-4 md:p-6">
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <label className="text-sm text-slate-600">
                Session
                <select
                  className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2"
                  value={selectedSessionId}
                  onChange={(e) => onSessionChange(e.target.value)}
                >
                  {overview.sessions.map((session) => (
                    <option key={session.id} value={session.id}>
                      {new Date(session.startDateTime).toLocaleString('en-GB', {
                        weekday: 'short',
                        day: '2-digit',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </option>
                  ))}
                </select>
              </label>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
                <p className="text-xs uppercase tracking-wide text-slate-500">Spots remaining</p>
                <p className="mt-1 font-semibold text-slate-900">
                  {selectedSession?.capacity === -1 ? 'Unlimited' : selectedSession?.spotsRemaining ?? 0}
                </p>
              </div>
              <label className="text-sm text-slate-600">
                Attendee first name
                <input
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                  value={form.childFirstName}
                  onChange={(e) => setForm((current) => ({ ...current, childFirstName: e.target.value }))}
                  data-testid="manual-booking-child-first-name"
                />
              </label>
              <label className="text-sm text-slate-600">
                Attendee last name
                <input
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                  value={form.childLastName}
                  onChange={(e) => setForm((current) => ({ ...current, childLastName: e.target.value }))}
                />
              </label>
              <label className="text-sm text-slate-600">
                Date of birth (optional)
                <input
                  type="date"
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                  value={form.childDateOfBirth}
                  onChange={(e) => setForm((current) => ({ ...current, childDateOfBirth: e.target.value }))}
                />
              </label>
              <label className="text-sm text-slate-600">
                Parent name (optional)
                <input
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                  value={form.parentName}
                  onChange={(e) => setForm((current) => ({ ...current, parentName: e.target.value }))}
                  placeholder="Optional"
                />
              </label>
              <label className="text-sm text-slate-600">
                Amount paid ({overview.event.currency})
                <input
                  inputMode="decimal"
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                  value={form.amount}
                  onChange={(e) => setForm((current) => ({ ...current, amount: e.target.value }))}
                  placeholder="0.00"
                  data-testid="manual-booking-amount"
                />
              </label>
            </div>

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Link
                to={backHref}
                className="rounded-lg border border-slate-300 px-4 py-2 text-center text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </Link>
              <button
                type="submit"
                className="rounded-lg bg-[#1F8F5F] px-4 py-2 text-sm font-semibold text-white hover:bg-[#18724c] disabled:opacity-60"
                disabled={saving}
                data-testid="manual-booking-submit"
              >
                {saving ? 'Adding...' : 'Add attendee'}
              </button>
            </div>
          </form>
        </section>
      )}
    </AdminShell>
  );
};

export default ActivityWalkInPage;
